import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Section } from '../components/ui/Section';
import { LegalModal } from '../components/LegalModal';
import { ExitIntentPopup } from '../components/ExitIntentPopup';
import { TrendingUpIcon, BrainIcon, MessageSquareIcon, DoorOpenIcon, CheckCircle2Icon, ArrowRightIcon, ShieldCheckIcon, AlertCircleIcon, TagIcon } from 'lucide-react';
export function SalesPage() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const checkoutUrl = 'https://mapaclaro.com/cart/52360055849249:1';
  const goToCheckout = () => {
    window.open(checkoutUrl, '_blank');
  };
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen font-sans text-gray-900" data-id="element-56">
      {/* Navigation / Header */}
      <header className="fixed top-0 w-full bg-white/90 backdrop-blur-md border-b border-gray-100 z-50" data-id="element-57">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between" data-id="element-58">
          <div className="font-bold text-xl text-emerald-900 tracking-tight" data-id="element-59">
            RELAB
          </div>
          <Button variant="primary" size="sm" onClick={scrollToPricing} data-id="element-60">
            Obtener Auditoría
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <Section className="pt-32 pb-16 md:pt-40 md:pb-24" background="white" data-id="element-61">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-62">
          <div className="space-y-8" data-id="element-63">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-sm font-semibold tracking-wide uppercase" data-id="element-64">
              Para mujeres profesionales en Colombia
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight" data-id="element-65">
              ¿Estás cansada de ser la profesional que{' '}
              <span className="text-emerald-800" data-id="element-66">más trabaja</span>, pero la que{' '}
              <span className="text-red-700" data-id="element-67">menos recibe</span>?
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed" data-id="element-68">
              Entras a la oficina, entregas resultados excepcionales, salvas el
              proyecto de la semana... y al final de mes, tu desprendible de
              pago sigue marcando la misma cifra de hace dos años.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-4" data-id="element-69">
              <Button variant="accent" size="lg" onClick={scrollToPricing} className="w-full sm:w-auto text-lg shadow-xl shadow-amber-600/20" data-id="element-70">
                QUIERO MI AUMENTO AHORA
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-71" />
              </Button>
              <p className="text-sm text-gray-500 mt-3 sm:mt-0 sm:ml-4 flex items-center" data-id="element-72">
                <ShieldCheckIcon className="h-4 w-4 mr-1 text-emerald-600" data-id="element-73" />
                Garantía de satisfacción
              </p>
            </div>
          </div>
          <div className="relative" data-id="element-74">
            <div className="absolute inset-0 bg-gradient-to-tr from-emerald-100 to-amber-50 rounded-2xl transform rotate-3 scale-105 -z-10" data-id="element-75"></div>
            <img src="https://cdn.magicpatterns.com/uploads/gpo3Vyxq6ThXZeVsMZzkhQ/salario_imparable.png" alt="El Método Salario Imparable en Tablet" className="rounded-xl shadow-2xl w-full object-cover border border-gray-100" data-id="element-76" />
          </div>
        </div>
      </Section>

      {/* Problem Section (The Gap) */}
      <Section background="gray" containerSize="md" data-id="element-77">
        <div className="text-center mb-12" data-id="element-78">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-79">
            La brecha de los $8M
          </h2>
          <div className="h-1 w-24 bg-amber-500 mx-auto rounded-full" data-id="element-80"></div>
        </div>

        <div className="prose prose-lg mx-auto text-gray-600" data-id="element-81">
          <p className="mb-6" data-id="element-82">
            Ves cómo otros (muchas veces hombres con menos preparación que tú)
            avanzan, negocian y obtienen bonos mientras a ti te dicen que{' '}
            <span className="font-semibold text-gray-900" data-id="element-83">
              "no hay presupuesto"
            </span>{' '}
            o que "esperemos a la próxima revisión".
          </p>
          <p className="mb-8" data-id="element-84">
            Sientes esa presión en el pecho. No es solo dinero; es la sensación
            de que tu talento está siendo liquidado a precio de saldo.
          </p>

          <Card className="bg-red-50 border-red-100 p-8 my-8" data-id="element-85">
            <div className="flex items-start gap-4" data-id="element-86">
              <AlertCircleIcon className="h-8 w-8 text-red-600 flex-shrink-0 mt-1" data-id="element-87" />
              <div data-id="element-88">
                <h3 className="text-xl font-bold text-red-900 mb-2" data-id="element-89">
                  El Problema Real
                </h3>
                <p className="text-red-800" data-id="element-90">
                  El mercado corporativo en Colombia no premia el esfuerzo.{' '}
                  <span className="font-bold" data-id="element-91">
                    Premia la capacidad de auditoría.
                  </span>{' '}
                  La mayoría de las mujeres profesionales fallan en la
                  negociación porque piden desde la necesidad o el mérito,
                  cuando deberían negociar desde el lucro generado.
                </p>
              </div>
            </div>
          </Card>

          <p className="text-center font-medium text-xl text-emerald-900" data-id="element-92">
            No es falta de presupuesto, es tu falta de un Protocolo de
            Auditoría.
          </p>
        </div>
      </Section>

      {/* Solution Section (The Mechanism) */}
      <Section background="emerald" className="relative overflow-hidden" data-id="element-93">
        <div className="absolute inset-0 opacity-10" data-id="element-94">
          <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-95">
            <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" data-id="element-96" />
          </svg>
        </div>

        <div className="relative z-10 text-center max-w-4xl mx-auto" data-id="element-97">
          <span className="text-emerald-300 font-semibold tracking-wider uppercase text-sm mb-4 block" data-id="element-98">
            La Solución Definitiva
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-99">
            Presentamos: Protocolo de Auditoría de Valor RELAB
          </h2>
          <p className="text-xl text-emerald-100 mb-10 max-w-2xl mx-auto" data-id="element-100">
            He creado un sistema técnico. Una guía paso a paso llamada{' '}
            <span className="text-amber-400 font-semibold" data-id="element-101">
              El Método Salario Imparable
            </span>
            . No vas a ir a "pedir" un aumento. Vas a ir a presentar una
            auditoría de valor que hace que negarte el ajuste sea
            financieramente irresponsable para la empresa.
          </p>
          <Button variant="accent" size="lg" onClick={scrollToPricing} data-id="element-102">
            Descubrir el Método
          </Button>
        </div>
      </Section>

      {/* What You Get Grid */}
      <Section containerSize="lg" data-id="element-103">
        <div className="text-center mb-16" data-id="element-104">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-105">
            Lo que obtendrás dentro del protocolo
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-106">
            Herramientas quirúrgicas diseñadas para desactivar las excusas de
            RRHH y Finanzas.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-107">
          <Card className="p-8 border-t-4 border-t-emerald-600" data-id="element-108">
            <div className="h-12 w-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-6" data-id="element-109">
              <TrendingUpIcon className="h-6 w-6 text-emerald-700" data-id="element-110" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-111">
              El Framework RELAB
            </h3>
            <p className="text-gray-600" data-id="element-112">
              Cómo traducir tus tareas diarias a millones de pesos en ahorro o
              ganancia para tu jefe. Convierte tu trabajo en números que no
              pueden ignorar.
            </p>
          </Card>

          <Card className="p-8 border-t-4 border-t-emerald-600" data-id="element-113">
            <div className="h-12 w-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-6" data-id="element-114">
              <BrainIcon className="h-6 w-6 text-emerald-700" data-id="element-115" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-116">
              Inteligencia de Mercado
            </h3>
            <p className="text-gray-600" data-id="element-117">
              Cómo saber cuánto están pagando realmente por tu cargo en Colombia
              (sin filtraciones incómodas). Datos reales para respaldar tu
              petición.
            </p>
          </Card>

          <Card className="p-8 border-t-4 border-t-emerald-600" data-id="element-118">
            <div className="h-12 w-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-6" data-id="element-119">
              <MessageSquareIcon className="h-6 w-6 text-emerald-700" data-id="element-120" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-121">
              El Guion de Hierro
            </h3>
            <p className="text-gray-600" data-id="element-122">
              Las palabras exactas para pedir la reunión, manejar el "no" y
              cerrar el acuerdo. Scripts palabra por palabra para eliminar los
              nervios.
            </p>
          </Card>

          <Card className="p-8 border-t-4 border-t-emerald-600" data-id="element-123">
            <div className="h-12 w-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-6" data-id="element-124">
              <DoorOpenIcon className="h-6 w-6 text-emerald-700" data-id="element-125" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-126">
              Plan de Salida
            </h3>
            <p className="text-gray-600" data-id="element-127">
              Cómo usar tu auditoría para conseguir una oferta externa que
              supere tu salario actual en un 20% si tu empresa actual no valora
              tu aporte.
            </p>
          </Card>
        </div>
      </Section>

      {/* Social Proof / Hooks Section */}
      <Section background="gray" containerSize="md" data-id="element-128">
        <div className="space-y-6" data-id="element-129">
          <h3 className="text-2xl font-bold text-center mb-8" data-id="element-130">
            La mentalidad que cambiará tu carrera
          </h3>
          <div className="bg-white p-6 rounded-lg shadow-sm flex gap-4" data-id="element-131">
            <CheckCircle2Icon className="h-6 w-6 text-emerald-600 flex-shrink-0" data-id="element-132" />
            <p className="text-gray-700 font-medium" data-id="element-133">
              "Dejé de esperar el 'buen trabajo' y empecé a recibir el 'pago
              justo'."
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm flex gap-4" data-id="element-134">
            <CheckCircle2Icon className="h-6 w-6 text-emerald-600 flex-shrink-0" data-id="element-135" />
            <p className="text-gray-700 font-medium" data-id="element-136">
              "LinkedIn dice que vales más. Tu cuenta bancaria dice lo
              contrario. Cambiemos eso."
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm flex gap-4" data-id="element-137">
            <CheckCircle2Icon className="h-6 w-6 text-emerald-600 flex-shrink-0" data-id="element-138" />
            <p className="text-gray-700 font-medium" data-id="element-139">
              "Tu trabajo habla por ti, pero tus números cierran el trato."
            </p>
          </div>
        </div>
      </Section>

      {/* Pricing / CTA Section */}
      <Section id="pricing" className="bg-emerald-900 text-white text-center" data-id="element-140">
        <div className="max-w-3xl mx-auto" data-id="element-141">
          <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-142">
            Deja de regalar tu talento hoy mismo
          </h2>
          <p className="text-xl text-emerald-100 mb-12" data-id="element-143">
            No permitas que otro año pase regalando tu tiempo. Obtén la
            herramienta que puede poner{' '}
            <span className="text-amber-400 font-bold" data-id="element-144">
              $8.000.000 adicionales
            </span>{' '}
            en tu bolsillo este año.
          </p>

          <div className="bg-white text-gray-900 rounded-2xl p-8 md:p-12 shadow-2xl max-w-lg mx-auto relative overflow-hidden" data-id="element-145">
            {/* Discount Badge */}
            <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg" data-id="element-146">
              <TagIcon className="h-3.5 w-3.5" data-id="element-147" />
              -74% DESCUENTO
            </div>

            <div className="text-sm text-gray-500 uppercase tracking-wide font-semibold mb-4" data-id="element-148">
              Oferta Especial por Lanzamiento
            </div>

            {/* Original Price (crossed out) */}
            <div className="flex items-center justify-center gap-2 mb-1" data-id="element-149">
              <span className="text-xl text-gray-400 line-through decoration-red-400 decoration-2" data-id="element-150">
                $77.000 COP
              </span>
            </div>

            {/* New Price */}
            <div className="flex items-baseline justify-center gap-1 mb-2" data-id="element-151">
              <span className="text-sm text-gray-500" data-id="element-152">Por solo</span>
            </div>
            <div className="flex items-baseline justify-center gap-2 mb-6" data-id="element-153">
              <span className="text-6xl font-bold text-emerald-900" data-id="element-154">
                $19.900
              </span>
              <span className="text-xl text-gray-500" data-id="element-155">COP</span>
            </div>

            <p className="text-gray-600 mb-8 pb-8 border-b border-gray-100" data-id="element-156">
              Menos de lo que cuesta un almuerzo, por un sistema que te pagará
              dividendos toda tu carrera.
            </p>

            <ul className="text-left space-y-4 mb-8" data-id="element-157">
              <li className="flex items-center gap-3" data-id="element-158">
                <CheckCircle2Icon className="h-5 w-5 text-emerald-600" data-id="element-159" />
                <span data-id="element-160">Acceso inmediato al Protocolo PDF</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-161">
                <CheckCircle2Icon className="h-5 w-5 text-emerald-600" data-id="element-162" />
                <span data-id="element-163">Plantillas de Excel editables</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-164">
                <CheckCircle2Icon className="h-5 w-5 text-emerald-600" data-id="element-165" />
                <span data-id="element-166">Guiones de negociación palabra por palabra</span>
              </li>
            </ul>

            <Button variant="accent" size="lg" className="w-full text-lg py-6 shadow-xl shadow-amber-600/20" onClick={goToCheckout} data-id="element-167">
              QUIERO MI AUMENTO AHORA
            </Button>
            <p className="text-xs text-gray-400 mt-4" data-id="element-168">
              Pago 100% seguro. Garantía de devolución de 30 días.
            </p>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="bg-emerald-950 py-10 text-sm" data-id="element-169">
        <div className="max-w-7xl mx-auto px-4" data-id="element-170">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6" data-id="element-171">
            <p className="text-emerald-400/60" data-id="element-172">
              © 2025 RELAB. Todos los derechos reservados.
            </p>
            <div className="flex items-center gap-6" data-id="element-173">
              <button onClick={() => setPrivacyOpen(true)} className="text-emerald-400/80 hover:text-emerald-300 transition-colors underline underline-offset-2" data-id="element-174">
                Política de Privacidad
              </button>
              <button onClick={() => setTermsOpen(true)} className="text-emerald-400/80 hover:text-emerald-300 transition-colors underline underline-offset-2" data-id="element-175">
                Términos y Condiciones
              </button>
            </div>
          </div>
          <p className="text-emerald-400/40 text-center text-xs" data-id="element-176">
            Este producto no garantiza resultados específicos. Los resultados
            pueden variar según la industria y la experiencia individual.
          </p>
        </div>
      </footer>

      {/* Legal Modals */}
      <LegalModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" data-id="element-177">
        <h3 className="font-bold text-gray-900 text-base" data-id="element-178">
          1. Información que Recopilamos
        </h3>
        <p data-id="element-179">
          Recopilamos información personal que usted nos proporciona
          directamente al realizar una compra, como su nombre, dirección de
          correo electrónico y datos de pago. Los datos de pago son procesados
          de forma segura por nuestro proveedor de pagos y no almacenamos
          información de tarjetas de crédito en nuestros servidores.
        </p>

        <h3 className="font-bold text-gray-900 text-base" data-id="element-180">
          2. Uso de la Información
        </h3>
        <p data-id="element-181">
          Utilizamos su información personal exclusivamente para: procesar y
          entregar su compra del producto digital, enviar comunicaciones
          relacionadas con su pedido, mejorar nuestros productos y servicios, y
          cumplir con obligaciones legales aplicables en la República de
          Colombia.
        </p>

        <h3 className="font-bold text-gray-900 text-base" data-id="element-182">
          3. Protección de Datos
        </h3>
        <p data-id="element-183">
          Implementamos medidas de seguridad técnicas y organizativas apropiadas
          para proteger su información personal contra acceso no autorizado,
          alteración, divulgación o destrucción. Cumplimos con la Ley 1581 de
          2012 (Ley de Protección de Datos Personales de Colombia) y sus
          decretos reglamentarios.
        </p>

        <h3 className="font-bold text-gray-900 text-base" data-id="element-184">
          4. Cookies y Tecnologías de Seguimiento
        </h3>
        <p data-id="element-185">
          Nuestro sitio web puede utilizar cookies y tecnologías similares para
          mejorar su experiencia de navegación y analizar el tráfico del sitio.
          Usted puede configurar su navegador para rechazar cookies, aunque esto
          puede afectar la funcionalidad del sitio.
        </p>

        <h3 className="font-bold text-gray-900 text-base" data-id="element-186">
          5. Compartir Información
        </h3>
        <p data-id="element-187">
          No vendemos, alquilamos ni compartimos su información personal con
          terceros, excepto cuando sea necesario para procesar su pago o cuando
          la ley lo requiera.
        </p>

        <h3 className="font-bold text-gray-900 text-base" data-id="element-188">6. Sus Derechos</h3>
        <p data-id="element-189">
          De acuerdo con la legislación colombiana, usted tiene derecho a
          conocer, actualizar, rectificar y solicitar la supresión de sus datos
          personales. Para ejercer estos derechos, puede contactarnos a través
          de los canales indicados en nuestro sitio.
        </p>

        <h3 className="font-bold text-gray-900 text-base" data-id="element-190">
          7. Cambios a esta Política
        </h3>
        <p data-id="element-191">
          Nos reservamos el derecho de actualizar esta política de privacidad en
          cualquier momento. Los cambios serán publicados en esta página con la
          fecha de última actualización.
        </p>

        <p className="text-xs text-gray-400 mt-6" data-id="element-192">
          Última actualización: Febrero 2025
        </p>
      </LegalModal>

      <LegalModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Términos y Condiciones" data-id="element-193">
        <h3 className="font-bold text-gray-900 text-base" data-id="element-194">
          1. Aceptación de los Términos
        </h3>
        <p data-id="element-195">
          Al acceder y utilizar este sitio web y al adquirir nuestros productos
          digitales, usted acepta estar sujeto a estos Términos y Condiciones.
          Si no está de acuerdo con alguno de estos términos, le rogamos que no
          utilice nuestros servicios.
        </p>

        <h3 className="font-bold text-gray-900 text-base" data-id="element-196">
          2. Descripción del Producto
        </h3>
        <p data-id="element-197">
          "El Método Salario Imparable" es un producto digital educativo en
          formato PDF que incluye guías, plantillas y scripts de negociación
          salarial. El producto se entrega en formato digital inmediatamente
          después de la confirmación del pago.
        </p>

        <h3 className="font-bold text-gray-900 text-base" data-id="element-198">3. Precio y Pago</h3>
        <p data-id="element-199">
          Los precios están expresados en Pesos Colombianos (COP) e incluyen los
          impuestos aplicables. El pago se procesa a través de plataformas de
          pago seguras. Una vez confirmado el pago, recibirá acceso inmediato al
          producto digital.
        </p>

        <h3 className="font-bold text-gray-900 text-base" data-id="element-200">
          4. Política de Reembolso
        </h3>
        <p data-id="element-201">
          Ofrecemos una garantía de devolución de 30 días. Si no está satisfecho
          con el producto, puede solicitar un reembolso completo dentro de los
          30 días posteriores a la compra, sin necesidad de justificación, de
          acuerdo con el derecho de retracto establecido en el Estatuto del
          Consumidor (Ley 1480 de 2011).
        </p>

        <h3 className="font-bold text-gray-900 text-base" data-id="element-202">
          5. Propiedad Intelectual
        </h3>
        <p data-id="element-203">
          Todo el contenido del producto, incluyendo textos, gráficos,
          plantillas y metodologías, está protegido por derechos de autor. La
          compra otorga una licencia personal e intransferible de uso. Queda
          prohibida la reproducción, distribución o reventa del contenido sin
          autorización expresa.
        </p>

        <h3 className="font-bold text-gray-900 text-base" data-id="element-204">
          6. Limitación de Responsabilidad
        </h3>
        <p data-id="element-205">
          Este producto es de carácter educativo e informativo. Los resultados
          pueden variar según las circunstancias individuales de cada usuario,
          incluyendo su industria, experiencia y mercado laboral. No
          garantizamos resultados financieros específicos.
        </p>

        <h3 className="font-bold text-gray-900 text-base" data-id="element-206">7. Ley Aplicable</h3>
        <p data-id="element-207">
          Estos términos se rigen por las leyes de la República de Colombia.
          Cualquier controversia será resuelta ante los tribunales competentes
          de Colombia, de acuerdo con la normativa vigente.
        </p>

        <p className="text-xs text-gray-400 mt-6" data-id="element-208">
          Última actualización: Febrero 2025
        </p>
      </LegalModal>

      {/* Exit Intent Popup */}
      <ExitIntentPopup data-id="element-209" />
    </div>;
}